import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronsUpDown, Check, type LucideIcon } from 'lucide-react';

interface DropdownItem {
    id?: string | number | null;
    label: string;
    value: any;
    [key: string]: any;
}

interface SingleSelectLocalSearchDropdownProps {
    label?: string;
    value?: string | number | null;
    options: DropdownItem[];
    onChange: (value: any) => void;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    icon?: LucideIcon;
    searchPlaceholder?: string;
    searchInputPlaceholder?: string;
    error?: string;
    required?: boolean;
    searchFields?: string[];
    buttonClassName?: string;
    disableClear?: boolean;
}

const SingleSelectLocalSearchDropdown = React.memo<SingleSelectLocalSearchDropdownProps>(({
    label,
    value,
    options,
    onChange,
    disabled = false,
    placeholder = "Select item",
    className = "",
    icon: Icon,
    searchInputPlaceholder = "Search...",
    error,
    required,
    searchFields = ['label'],
    buttonClassName = "",
    disableClear = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const [searchTerm, setSearchTerm] = useState('');

    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const filteredItems = options.filter(item => {
        if (!searchTerm.trim()) return true;
        const lowerTerm = searchTerm.toLowerCase();
        return searchFields.some(field => {
            const val = item[field];
            return val && String(val).toLowerCase().includes(lowerTerm);
        });
    });

    const selectedItem = options.find(opt => opt.value === value);

    const calculateDropdownPosition = useCallback(() => {
        if (!buttonRef.current) return;
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownHeight = 320;
        const windowHeight = window.innerHeight;
        const spaceBelow = windowHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        let position: 'bottom' | 'top' = 'bottom';

        const menuWidth = Math.max(buttonRect.width, 250);
        const windowWidth = window.innerWidth;
        let left = buttonRect.left;

        // Ensure menu doesn't overflow right edge of screen
        if (left + menuWidth > windowWidth - 16) {
            left = Math.max(16, windowWidth - menuWidth - 16);
        }

        let style: React.CSSProperties = {
            width: `${menuWidth}px`,
            left: `${left}px`,
            position: 'fixed'
        };

        if (spaceBelow < dropdownHeight + 20 && spaceAbove > dropdownHeight + 20) {
            position = 'top';
            style.bottom = `${windowHeight - buttonRect.top + 8}px`;
        } else {
            position = 'bottom';
            style.top = `${buttonRect.bottom + 8}px`;
        }

        setDropdownPosition(position);
        setMenuStyle(style);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                dropdownRef.current && !dropdownRef.current.contains(target) &&
                menuRef.current && !menuRef.current.contains(target)
            ) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const updatePosition = useCallback(() => {
        if (isOpen) {
            calculateDropdownPosition();
        }
    }, [isOpen, calculateDropdownPosition]);

    useEffect(() => {
        if (isOpen) {
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);
        }
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, updatePosition]);

    const toggleDropdown = () => {
        if (disabled) return;
        if (!isOpen) {
            calculateDropdownPosition();
            setSearchTerm('');
        }
        setIsOpen(!isOpen);
    };

    const handleSelect = (item: DropdownItem) => {
        onChange(item.value);
        setIsOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
    };

    return (
        <div className={`space-y-1.5 ${className}`} ref={dropdownRef}>
            {label && (
                <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                    {Icon && <Icon className="w-3.5 h-3.5 text-white" aria-hidden="true" />}
                    {label}
                    {required && <span className='text-red-500'>*</span>}
                </label>
            )}

            <button
                ref={buttonRef}
                type="button"
                onClick={toggleDropdown}
                disabled={disabled}
                className={`group relative w-full cursor-pointer rounded-xl border-2 bg-slate-50 py-1.5 pl-3 pr-9 text-left text-[10px] shadow-sm transition-all duration-300
                    ${disabled ? 'opacity-50 cursor-not-allowed border-slate-100' : 'hover:border-slate-200 hover:bg-white'}
                    ${isOpen ? 'border-[#422568] bg-white ring-4 ring-[#422568]/10' : error ? 'border-red-100' : 'border-slate-100'}
                    focus:outline-none ${buttonClassName}`}
            >
                <div className="flex items-center gap-2 overflow-hidden min-w-0 flex-1">
                    {selectedItem ? (
                        <span className="truncate text-slate-800 font-bold leading-tight uppercase">
                            {selectedItem.label}
                        </span>
                    ) : (
                        <span className="text-slate-400 font-bold leading-tight truncate uppercase">{placeholder}</span>
                    )}
                </div>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
                    {value && !disabled && !disableClear && (
                        <button
                            onClick={handleClear}
                            className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
                            type="button"
                        >
                            <X className="w-3 h-3 text-slate-400 hover:text-red-500" />
                        </button>
                    )}
                    <span className="pointer-events-none flex items-center px-1">
                        <ChevronsUpDown className={`h-3.5 w-3.5 text-slate-400 transform transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#422568]' : ''}`} />
                    </span>
                </div>
            </button>

            {isOpen && createPortal(
                <div
                    ref={menuRef}
                    className={`fixed z-[99999] bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100 ${dropdownPosition === 'top' ? 'origin-bottom' : 'origin-top'}`}
                    style={{ ...menuStyle, maxHeight: '320px' }}
                >
                    <div className="p-2 border-b border-slate-100 bg-slate-50/50 sticky top-0 z-10">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full bg-white border border-slate-200 rounded-lg pl-7 pr-2.5 py-1 text-[10px] text-slate-800 font-bold placeholder-slate-400 focus:border-[#422568] outline-none transition-all uppercase"
                                placeholder={searchInputPlaceholder}
                                autoFocus
                            />
                        </div>
                    </div>

                    <div className="overflow-y-auto max-h-[260px] custom-scroll">
                        {filteredItems.length > 0 ? (
                            <div className="p-1">
                                {filteredItems.map((item, idx) => {
                                    const isSelected = item.value === value;
                                    return (
                                        <button
                                            key={`${item.value}-${idx}`}
                                            type="button"
                                            onClick={() => handleSelect(item)}
                                            className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-[10px] transition-all group ${isSelected ? 'bg-[#422568] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                        >
                                            <span className={`truncate w-full text-left uppercase ${isSelected ? 'font-black' : 'font-bold'}`}>
                                                {item.label}
                                            </span>
                                            {isSelected && <Check className="w-3.5 h-3.5 shrink-0 text-[#422568]" />}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="p-8 text-center">
                                <div className="p-3 bg-slate-50 rounded-full w-fit mx-auto mb-3">
                                    <Search className="w-4 h-4 text-slate-300" />
                                </div>
                                <p className="text-[9px] text-slate-400 font-bold mt-1 uppercase">Try a different search term</p>
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}

            {error && <span className='text-red-500 text-[10px] font-black uppercase tracking-tight mt-1 flex items-center gap-1.5'>{error}</span>}
        </div>
    );
});

SingleSelectLocalSearchDropdown.displayName = 'SingleSelectLocalSearchDropdown';

export default SingleSelectLocalSearchDropdown;
